import React, { useEffect, useState, useRef } from 'react';
import { Download, Table, List, Loader2 } from 'lucide-react';
import { Toast, ToastProps } from './Toast';
import { Button } from './SubscriptionAppButton';
interface ExportButtonProps {
  onExport?: (type: string) => Promise<void>;
  className?: string;
  variant?: 'orders' | 'contacts' | 'subscriptions' | 'tasks';
}
export function ExportButton({
  onExport,
  className = '',
  variant = 'orders'
}: ExportButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeExport, setActiveExport] = useState<string | null>(null);
  const [toasts, setToasts] = useState<ToastProps[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleExport = async (type: string, label: string) => {
    setActiveExport(type);
    // Simulate export delay (1.5s)
    await new Promise(resolve => setTimeout(resolve, 1500));
    if (onExport) {
      await onExport(type);
    }
    setActiveExport(null);
    setIsOpen(false);
    // Show success toast
    const newToast: ToastProps = {
      id: Date.now().toString(),
      type: 'success',
      title: 'Export complete',
      message: `${label} has been downloaded successfully.`,
      onClose: removeToast
    };
    setToasts(prev => [...prev, newToast]);
  };
  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };
  return <div className="relative" ref={dropdownRef} data-id="element-1470">
      {/* Trigger Button */}
      <Button variant="outline" size="md" onClick={() => setIsOpen(!isOpen)} iconLeft={<Download className="w-4 h-4" data-id="element-1472" />} className={isOpen ? 'bg-[#F8FAFC] ring-2 ring-[#E0F2FE] border-[#0EA5E9]' : ''} data-id="element-1471">
        Export
      </Button>

      {/* Dropdown Menu */}
      {isOpen && <div className="absolute top-full right-0 mt-2 w-[280px] bg-white border border-[#E2E8F0] rounded-lg shadow-[0_4px_12px_rgba(15,23,42,0.1)] z-[1000] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-1473">
          {variant === 'orders' && <>
              {/* Orders Page Menu */}
              <div className="py-2" data-id="element-1474">
                <MenuItem icon={<Table className="w-5 h-5" data-id="element-1476" />} label="Export Current View (CSV)" onClick={() => handleExport('current_view', 'Current view')} isLoading={activeExport === 'current_view'} disabled={activeExport !== null} data-id="element-1475" />
                <MenuItem icon={<List className="w-5 h-5" data-id="element-1478" />} label="Export All Orders (CSV)" onClick={() => handleExport('all_orders', 'All orders list')} isLoading={activeExport === 'all_orders'} disabled={activeExport !== null} data-id="element-1477" />
              </div>
            </>}

          {variant === 'contacts' && <>
              {/* Contacts Page Menu */}
              <div className="py-2" data-id="element-1479">
                <MenuItem icon={<Table className="w-5 h-5" data-id="element-1481" />} label="Export Current View (CSV)" onClick={() => handleExport('current_view', 'Current view')} isLoading={activeExport === 'current_view'} disabled={activeExport !== null} data-id="element-1480" />
                <MenuItem icon={<List className="w-5 h-5" data-id="element-1483" />} label="Export All Contacts (CSV)" onClick={() => handleExport('all_contacts', 'All contacts list')} isLoading={activeExport === 'all_contacts'} disabled={activeExport !== null} data-id="element-1482" />
              </div>
            </>}

          {variant === 'subscriptions' && <>
              {/* Subscriptions/Activity Log Page Menu */}
              <div className="py-2" data-id="element-1484">
                <MenuItem icon={<Table className="w-5 h-5" data-id="element-1486" />} label="Export Current View (CSV)" onClick={() => handleExport('current_view', 'Current view')} isLoading={activeExport === 'current_view'} disabled={activeExport !== null} data-id="element-1485" />
                <MenuItem icon={<List className="w-5 h-5" data-id="element-1488" />} label="Export All Events (CSV)" onClick={() => handleExport('all_events', 'All events list')} isLoading={activeExport === 'all_events'} disabled={activeExport !== null} data-id="element-1487" />
              </div>
            </>}

          {variant === 'tasks' && <>
              {/* Tasks Page Menu */}
              <div className="py-2" data-id="element-1489">
                <MenuItem icon={<Table className="w-5 h-5" data-id="element-1491" />} label="Export Current View (CSV)" onClick={() => handleExport('current_view', 'Current view')} isLoading={activeExport === 'current_view'} disabled={activeExport !== null} data-id="element-1490" />
                <MenuItem icon={<List className="w-5 h-5" data-id="element-1493" />} label="Export All Tasks (CSV)" onClick={() => handleExport('all_tasks', 'All tasks list')} isLoading={activeExport === 'all_tasks'} disabled={activeExport !== null} data-id="element-1492" />
              </div>
            </>}
        </div>}

      {/* Toasts */}
      {toasts.map(toast => <Toast key={toast.id} {...toast} onClose={removeToast} data-id="element-1494" />)}
    </div>;
}
interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  subtext?: string;
  onClick: () => void;
  isLoading?: boolean;
  disabled?: boolean;
}
function MenuItem({
  icon,
  label,
  subtext,
  onClick,
  isLoading,
  disabled
}: MenuItemProps) {
  return <button onClick={onClick} disabled={disabled} className={`
        w-full text-left px-4 py-3 flex items-start gap-3
        transition-colors duration-150 group
        ${disabled && !isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#F8FAFC] cursor-pointer'}
      `} data-id="element-1495">
      <div className={`
        flex-shrink-0 mt-0.5 transition-colors
        ${isLoading ? 'text-[#0EA5E9]' : 'text-[#94A3B8] group-hover:text-[#0EA5E9]'}
      `} data-id="element-1496">
        {isLoading ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-1497" /> : icon}
      </div>
      <div data-id="element-1498">
        <div className={`
          text-[14px] font-medium leading-tight
          ${isLoading ? 'text-[#0EA5E9]' : 'text-[#0F172A]'}
        `} data-id="element-1499">
          {isLoading ? 'Generating...' : label}
        </div>
        {subtext && !isLoading && <div className="text-[12px] text-[#94A3B8] mt-1 leading-tight" data-id="element-1500">
            {subtext}
          </div>}
      </div>
    </button>;
}